import { useTexture } from '@react-three/drei';
import { GroupProps, useFrame } from '@react-three/fiber';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import {
  Group,
  Mesh,
  PerspectiveCamera,
  ShaderMaterial,
  Texture,
  Uniform,
  Vector2,
} from 'three';

import { publicAssets } from 'utils/gcs';

import {
  scaleBackground,
  WEATHER_SCREEN_PREFIX,
} from '../WeatherScreens.utils';
import fragmentShader from './shader.frag';
import vertexShader from './shader.vert';

interface SunScreenProps extends GroupProps {
  backgroundTexture: Texture;
}

const SunScreen = forwardRef<Group, SunScreenProps>(
  ({ backgroundTexture, ...props }, ref) => {
    const backgroundRef = useRef<Mesh>(null!);
    const screenRef = useRef<Group>(null!);

    const randomnessTex = useTexture(publicAssets('/effects/random.jpeg'));
    const materialRef = useRef<ShaderMaterial>(
      new ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms: {
          uRandomTexture: new Uniform(randomnessTex),
          uResolution: { value: new Vector2(1, 1) },
          uTime: { value: 0 },
          uBackground: new Uniform(backgroundTexture),
          uTarget: { value: new Vector2(0.4, 0.3) },
        },
      })
    );

    useFrame(({ camera, size, clock }) => {
      if (!screenRef.current || !backgroundRef.current || !backgroundTexture)
        return;

      scaleBackground({
        mesh: backgroundRef.current,
        camera: camera as PerspectiveCamera,
        size,
        texture: backgroundTexture,
        positionZ: screenRef.current.position.z,
      });

      materialRef.current.uniforms.uRandomTexture = new Uniform(randomnessTex);
      materialRef.current.uniforms.uBackground = new Uniform(backgroundTexture);
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime();
      materialRef.current.uniforms.uResolution.value.set(
        backgroundTexture.image.width,
        backgroundTexture.image.height
      );
    });

    useImperativeHandle(ref, () => screenRef.current);

    return (
      <group name={WEATHER_SCREEN_PREFIX + 'sunny'} ref={screenRef} {...props}>
        <mesh name="background" ref={backgroundRef} rotation={[Math.PI, 0, 0]}>
          <planeGeometry args={[1, 1]} />
          <primitive object={materialRef.current} attach="material" />
        </mesh>
      </group>
    );
  }
);

SunScreen.displayName = 'SunScreen';
export default SunScreen;
